import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
    AppBlockPort,
    AppBlockLinkParameter,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';
import * as simulator from './simulator';

export default class MathNode extends BaseNode {
    constructor() {
        super();
        this.name = 'Custom Function';
        this.icon = 'MdFunctions';
        this.id = 'node_math_compound';
        this.linkedFeature = 'general';

        this.inputs = [
            {
                name: 'Set',
                portId: 'set_value',
                params: [],
            },
        ];

        this.outputs = [
            {
                name: 'Set Complete',
                portId: 'math_done',
                params: [] as AppBlockLinkParameter[],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'VARIABLE',
                name: 'Variable',
                settingType: SettingType.OPTION,
                required: false,
                refId: 'variable',
                default: '',
                options: [],
            },
            {
                id: 'FUNCNAME',
                name: 'Function Name',
                settingType: SettingType.NAME,
                required: true,
                default: 'custom_function',
            },
            {
                id: 'CODE',
                name: 'Code',
                settingType: SettingType.CODE,
                required: true,
                default: '',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingValue(settingId: string): string {
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const project = this.project;
            const FUNCNAME = this.getSettingValue('FUNCNAME');
            const VARIABLE = project.getFeature(this.getSettingValue('VARIABLE'));
            if (VARIABLE) {
                return `{{$${VARIABLE.getSettingValue('ID')}}} = ${FUNCNAME}()`;
            }
            return `${FUNCNAME}()`;
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const project: ProjectConfiguration = this.project;
        const VARIABLE = project.getFeature(this.getSettingValue('VARIABLE'));
        if (VARIABLE) {
            let variableValue = '';
            const links = project.getNodeLinks(this, 'set_value');
            if (links.length > 0) {
                const link = links[0];
                variableValue = project.getNodeParameter(link, 'value');
            }
            return `Set {{$${VARIABLE.getSettingValue('ID')}}} to`;
        }
        return this.name;
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }

    getDetails(): string {
        return `#### Math (Advanced)
Performs calculations in a function, and assigns the result to a variable.
Note that the value must be assigned to the variable with the same name as the function.

\`\`\`
function do_calc() as float
do_calc = TEMPERATURE1 - TEMPERATURE2
end function
\`\`\`
`;
    }
}
